import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Scale, CalendarDays, CircleDollarSign, LayoutTemplate } from 'lucide-react';
import {
  apiClient,
  type ImportKind,
  type ImportJobsListResponse,
} from '@/lib/api-client';

interface KindCard {
  kind: ImportKind;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface TemplateCard {
  id: 'templates';
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  route: string;
}

const CARDS: KindCard[] = [
  {
    kind: 'clients',
    label: 'Pessoas',
    description: 'Clientes PF e PJ com contatos, endereços e documentos.',
    icon: Users,
  },
  {
    kind: 'cases',
    label: 'Processos',
    description: 'Processos judiciais com partes, números CNJ e movimentações.',
    icon: Scale,
  },
  {
    kind: 'tasks',
    label: 'Compromissos',
    description: 'Tarefas, audiências, reuniões e eventos da agenda.',
    icon: CalendarDays,
  },
  {
    kind: 'financial',
    label: 'Financeiro',
    description: 'Lançamentos de receita, despesa e transferência.',
    icon: CircleDollarSign,
  },
];

const TEMPLATE_CARD: TemplateCard = {
  id: 'templates',
  label: 'Modelos',
  description: 'Modelos de documentos em PDF exportados do ADVBOX.',
  icon: LayoutTemplate,
  route: '/escritorio/configuracoes/importacao/templates',
};

function useKindStats(kind: ImportKind) {
  const [data, setData] = useState<ImportJobsListResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .importsList({ kind, pageSize: 1 })
      .then((r) => { if (!cancelled) setData(r); })
      .catch(() => { /* silencioso */ });
    return () => { cancelled = true; };
  }, [kind]);

  return data;
}

function KindCardItem({ card }: { card: KindCard }) {
  const navigate = useNavigate();
  const stats = useKindStats(card.kind);
  const Icon = card.icon;

  return (
    <button
      type="button"
      onClick={() => navigate(`/escritorio/configuracoes/importacao/${card.kind}`)}
      className="group flex flex-col gap-4 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 text-left transition hover:border-[var(--accent)] hover:shadow-sm"
    >
      <div className="flex items-start justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[color-mix(in_oklch,var(--accent)_12%,transparent)]">
          <Icon className="h-5 w-5 text-[var(--accent)]" />
        </span>
        {stats !== null && (
          <span className="text-xs text-[var(--fg-muted)]">
            {stats.total} importação{stats.total !== 1 ? 'ões' : ''}
          </span>
        )}
      </div>
      <div>
        <h3 className="font-semibold text-[var(--fg)] group-hover:text-[var(--accent)]">
          {card.label}
        </h3>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">
          {card.description}
        </p>
      </div>
    </button>
  );
}

function StaticCard({ card }: { card: TemplateCard }) {
  const navigate = useNavigate();
  const Icon = card.icon;

  return (
    <button
      type="button"
      onClick={() => navigate(card.route)}
      className="group flex flex-col gap-4 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 text-left transition hover:border-[var(--accent)] hover:shadow-sm"
    >
      <div className="flex items-start justify-between">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[color-mix(in_oklch,var(--accent)_12%,transparent)]">
          <Icon className="h-5 w-5 text-[var(--accent)]" />
        </span>
      </div>
      <div>
        <h3 className="font-semibold text-[var(--fg)] group-hover:text-[var(--accent)]">
          {card.label}
        </h3>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">
          {card.description}
        </p>
      </div>
    </button>
  );
}

export default function ImportacaoIndexPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-[var(--fg)]">
          Importação de dados
        </h1>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">
          Importe dados exportados do ADVBOX. Selecione o tipo de dado para começar.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {CARDS.map((card) => (
          <KindCardItem key={card.kind} card={card} />
        ))}
        <StaticCard card={TEMPLATE_CARD} />
      </div>
    </div>
  );
}
